/**
 * use-adapty.ts — Adapty 集成 API 封装（凭证管理 + catalog 同步）
 *
 * 对应后端：
 *  - /api/v1/integrations/adapty/credentials*（admin+）：按 App 录 Adapty Secret Key。
 *    明文 Key 仅提交、**永不回显**；列表只回 key_tail 掩码 + status + 同步统计。
 *  - /api/v1/payments/adapty/catalog（operator+）：placements（Adapty Export 同步）
 *    + vendor_product_id 候选（C 端观测表去重，供 PaywallPage / ProductMetadataEditor）。
 *
 * 每方法 = 一次 apiRequest（自动带 JWT + X-Request-Id + 解 envelope 返 data，失败抛 AdminApiError）。
 * 请求/响应类型一律从 @novel-admin/shared 取（禁 web 侧重定义）。
 */

import type {
  AdaptyCatalogResponse,
  AdaptyCredentialResponse,
  AdaptyCredentialTestResponse,
  UpsertAdaptyCredentialRequest,
} from '@novel-admin/shared';
import { apiRequest } from '~/api/client';

export const adaptyApi = {
  /** 列已配置 App 凭证（掩码；admin+） */
  listCredentials: () =>
    apiRequest<AdaptyCredentialResponse[]>({
      method: 'GET',
      path: '/api/v1/integrations/adapty/credentials',
    }),
  /** 录 / 换 Adapty Secret Key（后端先 ping 验证再加密存；明文仅提交） */
  upsertCredential: (appId: string, body: UpsertAdaptyCredentialRequest) =>
    apiRequest<AdaptyCredentialResponse>({
      method: 'PUT',
      path: `/api/v1/integrations/adapty/credentials/${appId}`,
      body,
    }),
  /** 移除凭证（软删，可再录入） */
  removeCredential: (appId: string) =>
    apiRequest<{ app_id: string }>({
      method: 'DELETE',
      path: `/api/v1/integrations/adapty/credentials/${appId}`,
    }),
  /** 测试连接（解密已存 Key + ping Adapty，更新 status） */
  testCredential: (appId: string) =>
    apiRequest<AdaptyCredentialTestResponse>({
      method: 'POST',
      path: `/api/v1/integrations/adapty/credentials/${appId}/test`,
    }),
  /** 同步 catalog（placement + vendor_product_id 候选）；refresh=true 旁路缓存强制重拉 */
  catalog: (appId: string, refresh = false) =>
    apiRequest<AdaptyCatalogResponse>({
      method: 'GET',
      path: '/api/v1/payments/adapty/catalog',
      query: refresh ? { app_id: appId, refresh: 'true' } : { app_id: appId },
    }),
};
